import React from 'react';
import { ActiveCustomer } from '../../types/organizations';

interface CustomerInfoCardProps {
  customer: ActiveCustomer;
}

const CustomerInfoCard: React.FC<CustomerInfoCardProps> = ({ customer }) => {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{customer.name}</h2>
          <div className="flex items-center space-x-4">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              customer.contact_status === 'ACTIVE' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {customer.contact_status}
            </span>
            {customer.tax_number && (
              <span className="text-sm text-gray-500">Tax: {customer.tax_number}</span>
            )}
          </div>
        </div>
      </div>

      {/* Invoice Summary */}
      <div className="border-t border-gray-200 pt-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Summary</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-900">
              {customer.invoice_metadata.outstanding_invoice_count}/{customer.invoice_metadata.last_12m_invoice_count}
            </div>
            <div className="text-blue-600 text-sm">LTM Invoices</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-900">
              ${customer.invoice_metadata.total_receivable.toLocaleString()}
            </div>
            <div className="text-green-600 text-sm">Receivable</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-900">
              ${customer.invoice_metadata.last_12m_value.toLocaleString()}
            </div>
            <div className="text-purple-600 text-sm">LTM Value</div>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="space-y-4 mt-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact Information</h3>
          
          {/* Email */}
          {customer.email_address && (
            <div className="flex items-center mb-3">
              <svg className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span className="text-gray-700">{customer.email_address}</span>
            </div>
          )}

          {/* Phone Numbers */}
          {customer.phones && customer.phones.length > 0 && customer.phones.some(phone => phone.number && phone.number.trim()) && (
            <div className="flex items-start mb-3">
              <svg className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <div className="flex-1">
                {customer.phones.filter(phone => phone.number && phone.number.trim()).map((phone, index) => (
                  <div key={index} className="text-gray-700 mb-1">
                    <span className="font-medium">{phone.type}:</span> {phone.number}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Addresses */}
          {customer.addresses && customer.addresses.length > 0 && (
            <div className="flex items-start">
              <svg className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div className="flex-1">
                {customer.addresses.map((address, index) => (
                  <div key={index} className="text-gray-700 mb-1">
                    <div className="font-medium">{address.type}:</div>
                    <div className="text-sm">
                      {address.full_address && <div>{address.full_address}</div>}
                      {address.attention_to && <div>Attn: {address.attention_to}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Additional Information */}
        <div className="border-t border-gray-200 pt-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Additional Information</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Default Currency:</span>
              <span className="ml-2 font-medium text-gray-900">{customer.default_currency || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-600">Created:</span>
              <span className="ml-2 font-medium text-gray-900">
                {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerInfoCard;
